import { useState, useEffect } from 'react'
import { Land, Zoning, ZoningType } from '@popety-io/types'
import { ZoningDataByType } from '../utils/zoningHelpers'

export interface UseZoningDataProps {
  land?: Land
  getZonesByIds: (type: ZoningType, ids: string[]) => Promise<Zoning[]>
  enabled?: boolean
}

export interface UseZoningDataReturn {
  data: ZoningDataByType | null
  /** Per-zone plot coverage (decimal 0..1), keyed by zone ES id. Summed across duplicate references. */
  coverageByZoneId: Record<string, number>
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Hook to fetch zoning data for all types based on land zoning ID references
 */
export const useZoningData = ({
  land,
  getZonesByIds,
  enabled = true,
}: UseZoningDataProps): UseZoningDataReturn => {
  const [data, setData] = useState<ZoningDataByType | null>(null)
  const [coverageByZoneId, setCoverageByZoneId] = useState<
    Record<string, number>
  >({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    if (!land || !enabled) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Extract IDs from land zoning references
      const generalIds = land.land_use_plans_general?.map((ref) => ref.id) || []
      const cantonalIds =
        land.land_use_plans_cantonal?.map((ref) => ref.id) || []
      const localIds = land.land_use_plans_local?.map((ref) => ref.id) || []
      const partialIds = land.land_use_plans_partial?.map((ref) => ref.id) || []

      // Fetch all zoning types in parallel
      const [general, cantonal, local, partial] = await Promise.all([
        getZonesByIds('general', generalIds),
        getZonesByIds('cantonal', cantonalIds),
        getZonesByIds('local', localIds),
        getZonesByIds('partial', partialIds),
      ])

      setData({
        general,
        cantonal,
        local,
        partial,
      })

      const coverage: Record<string, number> = {}
      const refArrays = [
        land.land_use_plans_general,
        land.land_use_plans_cantonal,
        land.land_use_plans_local,
        land.land_use_plans_partial,
      ]
      for (const refs of refArrays) {
        for (const ref of refs || []) {
          if (ref.id && typeof ref.cover_area_perc === 'number') {
            coverage[ref.id] = (coverage[ref.id] || 0) + ref.cover_area_perc
          }
        }
      }
      setCoverageByZoneId(coverage)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch zoning data'
      setError(message)
      console.error('Error fetching zoning data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [land, enabled])

  return {
    data,
    coverageByZoneId,
    loading,
    error,
    refetch: fetchData,
  }
}
